import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { showToast } from '@/config/toast.config';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle as CheckCircleIcon, Images as ImagesIcon, ChatCircleDots as ChatIcon, FilmSlate as FilmSlateIcon } from '@phosphor-icons/react';
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';
import Alert from '@/components/common/Alert/Alert';
import ImageGalleryOverlay from '@/components/common/ImageGalleryOverlay/ImageGalleryOverlay';

const API_BASE = import.meta.env.VITE_API_BASE_URL;

// Videos come through the same photo list as images (CDN .mp4/.mov/.webm).
// An <img> can't render them, so the thumbnail is the FilmSlate tile used
// everywhere else in stoc; the gallery overlay already plays them.
const isVideoUrl = (url) => /\.(mp4|mov|webm)(?:$|\?)/i.test(url || '');

const MediaThumb = ({ url, alt, onClick }) => (
  isVideoUrl(url) ? (
    <button
      type="button"
      onClick={onClick}
      className="flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-lg bg-gray-100 text-gray-500"
      aria-label="Redă videoclipul"
    >
      <FilmSlateIcon size={32} weight="duotone" />
      <span className="text-xs">Video</span>
    </button>
  ) : (
    <img
      draggable={false}
      src={url}
      alt={alt}
      loading="lazy"
      className="aspect-square w-full cursor-pointer rounded-lg object-cover"
      onClick={onClick}
    />
  )
);

MediaThumb.propTypes = {
  url: PropTypes.string.isRequired,
  alt: PropTypes.string,
  onClick: PropTypes.func,
};

const formatPrice = (price, currency) => {
  const parsed = Number(price);
  if (price == null || !Number.isFinite(parsed) || parsed <= 0) return null;
  return `${parsed.toFixed(2).replace(/\.?0+$/, '').replace('.', ',')} ${currency || ''}`.trim();
};

// Public sale-confirmation page (c.dezshop.ro/comanda/:token): the photo
// gallery the operator selected + ONE action — "Confirmă comanda". Any change
// requests go over WhatsApp, so there is deliberately nothing else to do here.
const SaleConfirmationPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [pageState, setPageState] = useState('loading'); // loading|active|confirmed|expired|invalid
  const [data, setData] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/public/sale-confirmation/${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (res.status === 404) { setPageState('invalid'); return; }
        if (res.status === 410) { setPageState('expired'); return; }
        if (!res.ok) { setPageState('invalid'); return; }
        const json = await res.json();
        setData(json);
        setPageState(json.status === 'confirmed' ? 'confirmed' : 'active');
        // Best-effort view counter — never blocks the page.
        fetch(`${API_BASE}/public/sale-confirmation/${encodeURIComponent(token)}/view`, { method: 'POST' }).catch(() => {});
      } catch {
        if (!cancelled) setPageState('invalid');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await fetch(`${API_BASE}/public/sale-confirmation/${encodeURIComponent(token)}/confirm`, { method: 'POST' });
      if (!res.ok) throw new Error('Nu am putut înregistra confirmarea. Reîncearcă.');
      setPageState('confirmed');
    } catch (error) {
      showToast.error(error, { disableCopy: true });
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (pageState === 'invalid' || pageState === 'expired') {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <h1 className="text-xl font-semibold text-gray-800">
          {pageState === 'expired' ? 'Linkul a expirat' : 'Link invalid'}
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          {pageState === 'expired'
            ? 'Acest link de confirmare nu mai este valabil. Te rugăm să ne scrii pe WhatsApp pentru unul nou.'
            : 'Linkul accesat nu există sau a fost înlocuit. Verifică mesajul primit de la noi.'}
        </p>
      </div>
    );
  }

  const allPhotos = [
    ...(data?.items || []).flatMap((item) => item.photos || []),
    ...(data?.other_photos || []),
  ];
  const galleryItems = allPhotos.map((url, i) => ({ url, thumbnail: url, msgId: i }));
  const openGallery = (url) => {
    const idx = allPhotos.indexOf(url);
    setGalleryIndex(idx >= 0 ? idx : 0);
    setGalleryOpen(true);
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 pb-32">
      <div className="mb-4">
        <h1 className="text-lg font-semibold text-gray-900">
          Comanda {data?.sale_number}
        </h1>
        <p className="text-sm text-gray-600">
          {data?.client_name ? `Bună ziua, ${data.client_name}! ` : ''}
          Verificați pozele pieselor pregătite pentru dumneavoastră{pageState === 'confirmed' ? '.' : ' și confirmați comanda.'}
        </p>
      </div>

      {data?.message_note && (
        <Card className="mb-4 border-blue-200 bg-blue-50">
          <CardContent className="px-4 py-3 text-sm text-blue-900 whitespace-pre-wrap">
            {data.message_note}
          </CardContent>
        </Card>
      )}

      {(data?.items || []).map((item, idx) => (
        <Card key={idx} className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-baseline justify-between gap-2 text-base">
              <span>{item.part_name || 'Piesă'}</span>
              <span className="whitespace-nowrap text-sm font-medium text-gray-600">
                {item.quantity && item.quantity !== 1 ? `${item.quantity} × ` : ''}
                {formatPrice(item.price, item.currency) || ''}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Notes the operator added for THIS part, above the photos. */}
            {item.defect_note && (
              <Alert type="warning" compact className="mb-3">
                <Alert.Header className="font-semibold">Mențiuni despre această piesă</Alert.Header>
                <Alert.Text className="whitespace-pre-wrap">{item.defect_note}</Alert.Text>
              </Alert>
            )}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {(item.photos || []).map((url) => (
                <MediaThumb
                  key={url}
                  url={url}
                  alt={item.part_name || 'piesă'}
                  onClick={() => openGallery(url)}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {(data?.other_photos || []).length > 0 && (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <ImagesIcon size={18} /> Alte poze
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {data.other_photos.map((url) => (
                <MediaThumb key={url} url={url} alt="piesă" onClick={() => openGallery(url)} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <p className="mb-24 flex items-start gap-2 text-xs text-gray-500">
        <ChatIcon size={16} className="mt-0.5 flex-shrink-0" />
        Dacă doriți modificări sau aveți întrebări, scrieți-ne direct în conversația de WhatsApp.
      </p>

      {/* Single, always-visible action. */}
      <div className="fixed inset-x-0 bottom-0 border-t bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto max-w-2xl">
          {pageState === 'confirmed' ? (
            <div className="flex items-center justify-center gap-2 rounded-lg bg-brand-primary/10 px-4 py-3 text-brand-primary-dark">
              <CheckCircleIcon size={22} weight="fill" className="text-brand-primary" />
              <span className="font-semibold">Comanda a fost confirmată. Vă mulțumim!</span>
            </div>
          ) : (
            <Button
              className="h-12 w-full bg-brand-primary text-base font-semibold text-white hover:bg-brand-primary-dark"
              onClick={handleConfirm}
              isLoading={confirming}
              disabled={confirming}
            >
              <CheckCircleIcon size={20} weight="fill" />
              Confirmă comanda
            </Button>
          )}
        </div>
      </div>

      <ImageGalleryOverlay
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        images={galleryItems}
        initialIndex={galleryIndex}
      />
    </div>
  );
};

export default SaleConfirmationPage;
